<script setup>
import { onMounted, computed, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@/stores/auth'
import { useUiStore } from '@/stores/ui'
import { useNotificationsStore } from '@/stores/notifications'
import { setUserForLogging } from '@/services/api'
import { AppHeader, AppSidebar, AppBottomNav, AppToast } from '@/components/layout'

const { locale } = useI18n()
const route = useRoute()
const authStore = useAuthStore()
const uiStore = useUiStore()
const notificationsStore = useNotificationsStore()

// Check if current route is auth page (no layout)
const isAuthPage = computed(() => {
  return ['login', 'register'].includes(route.name)
})

// Initialize app
onMounted(async () => {
  // Initialize UI store (theme)
  uiStore.init()
  
  // Sync locale
  locale.value = uiStore.locale
  
  // Initialize auth
  authStore.init()
  
  // Fetch notifications count if authenticated
  if (authStore.isAuthenticated) {
    notificationsStore.fetchUnreadCount()
  }
})

// Watch for auth changes
watch(() => authStore.user, (user) => {
  setUserForLogging(user)
  
  if (user) {
    notificationsStore.fetchUnreadCount()
  } else {
    notificationsStore.clearNotifications()
  }
})

// Watch for locale changes
watch(() => uiStore.locale, (newLocale) => {
  locale.value = newLocale
})
</script>

<template>
  <div id="app" :class="{ dark: uiStore.isDark }">
    <!-- Auth pages (no layout) -->
    <template v-if="isAuthPage">
      <router-view />
    </template>
    
    <!-- Main app with layout -->
    <template v-else>
      <AppSidebar />
      
      <div class="app-layout">
        <AppHeader />
        
        <main class="main-content">
          <router-view v-slot="{ Component }">
            <transition name="page" mode="out-in">
              <component :is="Component" />
            </transition>
          </router-view>
        </main>
      </div>
      
      <AppBottomNav />
    </template>
    
    <!-- Toast notifications -->
    <AppToast />
  </div>
</template>

<style>
/* CSS Variables - Light Theme */
:root {
  --color-primary: #4CAF50;
  --color-primary-dark: #388E3C;
  --color-primary-light: rgba(76, 175, 80, 0.1);
  --color-secondary: #FF9800;
  --color-background: #ffffff;
  --color-background-secondary: #f5f5f5;
  --color-text: #333333;
  --color-text-secondary: #757575;
  --color-border: #e0e0e0;
  --color-danger: #dc3545;
  --color-danger-light: rgba(220, 53, 69, 0.1);
  --color-success: #28a745;
  --color-success-light: rgba(40, 167, 69, 0.1);
  --color-warning: #ffc107;
  --color-warning-light: rgba(255, 193, 7, 0.1);
  --color-info: #17a2b8;
  --color-info-light: rgba(23, 162, 184, 0.1);
  --color-shadow: rgba(0, 0, 0, 0.1);
  --color-overlay: rgba(0, 0, 0, 0.5);
}

/* Dark Theme - применяется к html.dark или #app.dark */
:root.dark,
.dark {
  --color-primary: #66BB6A;
  --color-primary-dark: #4CAF50;
  --color-primary-light: rgba(102, 187, 106, 0.15);
  --color-secondary: #FFB74D;
  --color-background: #1a1a2e;
  --color-background-secondary: #16213e;
  --color-text: #e8e8e8;
  --color-text-secondary: #a0a0a0;
  --color-border: #2d3a5a;
  --color-danger: #f87171;
  --color-danger-light: rgba(248, 113, 113, 0.15);
  --color-success: #4ade80;
  --color-success-light: rgba(74, 222, 128, 0.15);
  --color-warning: #fbbf24;
  --color-warning-light: rgba(251, 191, 36, 0.15);
  --color-info: #38bdf8;
  --color-info-light: rgba(56, 189, 248, 0.15);
  --color-shadow: rgba(0, 0, 0, 0.3);
  --color-overlay: rgba(0, 0, 0, 0.7);
}

/* Reset & Base */
*,
*::before,
*::after {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

html {
  font-size: 16px;
  scroll-behavior: smooth;
}

body {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
  background-color: var(--color-background);
  color: var(--color-text);
  line-height: 1.6;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  min-height: 100vh;
}

#app {
  min-height: 100vh;
}

.app-layout {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}

/* На десктопе layout сдвигается вправо под сайдбар */
@media (min-width: 1025px) {
  .app-layout {
    margin-left: 240px;
  }
}

.main-content {
  flex: 1;
  padding-bottom: calc(80px + env(safe-area-inset-bottom)); /* Space for bottom nav on mobile */
}

@media (min-width: 1025px) {
  .main-content {
    padding-bottom: 20px;
  }
}

/* Page transitions */
.page-enter-active,
.page-leave-active {
  transition: opacity 0.2s ease;
}

.page-enter-from,
.page-leave-to {
  opacity: 0;
}

/* Links */
a {
  color: var(--color-primary);
}

/* Form elements */
input,
textarea,
select,
button {
  font-family: inherit;
  font-size: 16px; /* Prevents zoom on iOS */
}

/* Улучшение тач-взаимодействия */
button,
a,
[role="button"] {
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
}

/* Отключаем выделение текста при долгом нажатии на кнопках */
button {
  -webkit-touch-callout: none;
  user-select: none;
}

/* Scrollbar styling */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  background: var(--color-background-secondary);
}

::-webkit-scrollbar-thumb {
  background: var(--color-border);
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: var(--color-text-secondary);
}

/* Selection */
::selection {
  background: var(--color-primary-light);
  color: var(--color-primary);
}

/* Focus outline */
:focus-visible {
  outline: 2px solid var(--color-primary);
  outline-offset: 2px;
}

/* Responsive helpers */

/* Print styles */
@media print {
  .app-header,
  .app-sidebar,
  .bottom-nav {
    display: none !important;
  }
  
  .main-content {
    padding: 0 !important;
  }
}
</style>
